import { useState, useContext } from 'react';
import { FormikContext, FormikContextType } from 'formik';
import { StoreProductValues } from '../CreateProductForm';
import { MediaLibrarySelector } from '../../../components/MediaLibrarySelector/MediaLibrarySelector';
import { FaPlus as PlusIcon, FaTimes as TrashIcon } from 'react-icons/fa';
import { Media } from '../../../../models/Media/Media';
import style from './product-images-step.module.scss';
import { MediaResponse } from '../../../../models/Media/contracts';

function ensureMediaInstances(list: (MediaResponse | Media)[]): Media[] {
  return list.map((item) => (item instanceof Media ? item : new Media(item)));
}

export default function ProductImages() {
  const { values, setFieldValue } =
    useContext<FormikContextType<StoreProductValues>>(FormikContext);

  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [isCoverOpen, setIsCoverOpen] = useState(false);

  // Ensure gallery_media is always an array of Media instances
  const galleryMedia: Media[] = ensureMediaInstances(values.gallery_media);

  // Remove image from gallery
  const removeImage = (id: number) => {
    const updatedGallery = galleryMedia.filter((img) => img.id !== id);
    setFieldValue('gallery_media', updatedGallery);
    setFieldValue(
      'gallery_media_ids',
      values.gallery_media_ids.filter((gid) => gid !== id)
    );
    if (values.cover_image_id === id) setFieldValue('cover_image_id', null);
  };

  // Find cover image as Media instance
  const coverMedia = galleryMedia.find(
    (img) => img.id === values.cover_image_id
  );

  return (
    <div className={style.imagesStep}>
      <h2>Slike proizvoda</h2>
      <div className={style.imagesLayout}>
        {/* Cover image selection */}
        <div className={style.coverCol}>
          <div className={style.coverLabel}>Naslovna slika</div>
          {coverMedia ? (
            <div className={style.coverPreview}>
              <img
                src={coverMedia.fullUrl}
                alt="Cover"
                className={style.image}
              />
              <button
                className={style.removeImage}
                type="button"
                onClick={() => setFieldValue('cover_image_id', null)}
                title="Ukloni naslovnu sliku"
              >
                <TrashIcon />
              </button>
            </div>
          ) : (
            <div className={style.coverEmpty}>
              <button
                className={style.addImageButton}
                type="button"
                onClick={() => setIsCoverOpen(true)}
              >
                <PlusIcon /> Izaberi naslovnu sliku
              </button>
            </div>
          )}

          {isCoverOpen && (
            <MediaLibrarySelector
              isOpen={isCoverOpen}
              onClose={() => setIsCoverOpen(false)}
              onSelect={(media) => {
                // Support both array and single selection
                const selected = Array.isArray(media) ? media[0] : media;
                if (selected && 'id' in selected) {
                  // Convert to Media class if needed
                  const newMedia =
                    selected instanceof Media ? selected : new Media(selected);
                  setFieldValue('cover_image_id', newMedia.id);
                  // If not in gallery, add it
                  if (!values.gallery_media_ids.includes(newMedia.id)) {
                    setFieldValue('gallery_media', [...galleryMedia, newMedia]);
                    setFieldValue('gallery_media_ids', [
                      ...values.gallery_media_ids,
                      newMedia.id
                    ]);
                  }
                  setIsCoverOpen(false);
                }
              }}
            />
          )}
        </div>

        {/* Gallery selection */}
        <div className={style.galleryCol}>
          <div className={style.galleryLabel}>
            Galerija slika
            <button
              className={style.addImageButton}
              type="button"
              onClick={() => setIsGalleryOpen(true)}
              style={{ marginLeft: 8 }}
            >
              <PlusIcon /> Dodaj slike iz galerije
            </button>
          </div>
          <div className={style.galleryGrid}>
            {galleryMedia.length === 0 && <p>Niste izabrali slike.</p>}
            {galleryMedia.map((img) => (
              <div
                key={img.id}
                className={
                  img.id === values.cover_image_id
                    ? style.galleryImageActive
                    : style.galleryImage
                }
              >
                <img src={img.fullUrl} alt="" />
                <button
                  className={style.removeImage}
                  type="button"
                  onClick={() => removeImage(img.id)}
                  title="Ukloni sliku"
                >
                  <TrashIcon />
                </button>
                {img.id !== values.cover_image_id && (
                  <button
                    type="button"
                    className={style.setCoverBtn}
                    onClick={() => setFieldValue('cover_image_id', img.id)}
                  >
                    Postavi kao naslovnu
                  </button>
                )}
                {img.id === values.cover_image_id && (
                  <span className={style.coverTag}>★ Cover</span>
                )}
              </div>
            ))}
          </div>
          {isGalleryOpen && (
            <MediaLibrarySelector
              isOpen={isGalleryOpen}
              multi
              onClose={() => setIsGalleryOpen(false)}
              onSelect={(mediaList) => {
                // Always flatten to an array
                const selected = Array.isArray(mediaList)
                  ? mediaList
                  : [mediaList];
                // Build a Map for quick lookup by id (existing + new)
                const allMedia = [...values.gallery_media, ...selected];
                // Deduplicate by id
                const dedupedMedia = Array.from(
                  new Map(allMedia.map((img) => [img.id, img])).values()
                );
                setFieldValue('gallery_media', dedupedMedia);
                setFieldValue(
                  'gallery_media_ids',
                  dedupedMedia.map((m) => m.id)
                );
                setIsGalleryOpen(false);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
